<template>
  <div
    class="text-light-950 justify-center rounded-full inline-flex items-center whitespace-nowrap shrink-0 leading-none bg-cover bg-center text-shadow-none"
    :class="[ getStyle(styles, 'container') ]"
    :style="theme?.base ? {
      backgroundColor: theme.base.background.xweak,
      color: theme.base.text.accent
    } : {
      ...(color === 'gradient-base' ? { backgroundImage: `url(${gradientBase})` } : {}),
      ...(color === 'gradient-long-distance' ? { backgroundImage: `url(${gradientLongDistance})` } : {}),
      ...(color === 'holo' ? { backgroundImage: `url(${holo})` } : {})
    }"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import holo from '@/assets/img/bg/holo.jpg'
import gradientBase from '@/assets/img/gradients/hard/gradient-2.jpg'
import gradientLongDistance from '@/assets/img/gradients/hard/gradient-7.jpg'
import { defineStyles, getStyle } from '@/utils/useStyles'
import { Theme } from '@/classes'

const props = withDefaults(defineProps<{
  color?: keyof typeof colorTypes
  size?: keyof typeof sizeTypes
  theme?: Theme | null
}>(), {
  theme: null,
  color: 'gray',
  size: 'base'
})

const colorTypes = {
  pear: 'bg-emerald-100 !text-emerald-800',

  royal: 'bg-royal-100 !text-royal-800',
  'royal-strong': 'bg-royal-200 !text-royal-950',
  'royal-fill': 'bg-royal-600 !text-white',

  ruby: 'bg-ruby-100 !text-ruby-800',
  'ruby-strong': 'bg-ruby-200 !text-light-950',
  'ruby-fill': 'bg-ruby-600 !text-white',

  flamingo: 'bg-flamingo-100 !text-flamingo-500',
  butterfly: 'bg-butterfly-100 !text-butterfly-500',
  whale: 'bg-whale-100 !text-whale-500',
  urchin: 'bg-urchin-100 !text-urchin-600',
  'urchin-strong': 'bg-urchin-200 !text-urchin-950',

  tiger: 'bg-tiger-50 !text-tiger-500',
  'tiger-strong': 'bg-tiger-100 !text-tiger-500',
  'tiger-fill': 'bg-tiger-500 !text-white',

  white: 'bg-white dark:bg-dark-800 dark:text-dark-50',
  slate: 'bg-charcoal-950 dark:bg-charcoal-100 dark:text-dark-950 text-white',
  'gradient-base': '!text-white',
  'gradient-long-distance': '!text-white',
  gray: 'bg-light-200 dark:bg-dark-600 dark:text-dark-100',
  grayWeak: 'bg-charcoal-200 bg-opacity-50 dark:bg-dark-700 dark:text-dark-500 text-charcoal-500 text-opacity-30',
  grayStrong: 'bg-charcoal-300/50 !text-charcoal-600',

  transparent: 'bg-white/60 text-light-950/50 dark:bg-dark-700/30 dark:text-dark-50',

  holo: 'bg-cover bg-center text-whale-800'
} as const

const sizeTypes = {
  xs: 'h-5 py-0.5 px-2 text-3xs font-bold',
  sm: 'h-6 py-0.5 px-2 text-3xs font-bold',
  base: 'py-2 px-3 text-2xs font-medium',
  md: 'py-2.5 px-3.5 min-w-[85px] text-sm font-medium',
  lg: 'py-3 px-4 min-w-[85px] text-base font-medium'
} as const

const styles = computed(() => defineStyles({
  container: {
    color: colorTypes,
    size: sizeTypes
  }
}, props))

</script>